import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Settings, MapPin, Power, Scale, Info } from 'lucide-react';
import { useOrgaConfigurationContext } from '@/app/providers';
import { OrgaConfigurationRepository } from '@/services/firebase/orga-configuration-repository';
import GooglePlacesSearch from '@/components/shared/GooglePlacesSearch';
import { Card, CardHeader, CardBody, Button } from '@/components/ui';

interface GeneralConfigurationFormData {
  name: string;
  address?: {
    street?: string;
    city?: string;
    postalCode?: string;
    country?: string;
  };
  legalInfo?: {
    companyName?: string;
    legalId?: string;
    vatNumber?: string;
    address?: {
      street?: string;
      city?: string;
      postalCode?: string;
      country?: string;
    };
  };
}

const GeneralConfiguration: React.FC = () => {
  const { t } = useTranslation();
  const { idorga } = useParams<{ idorga: string }>();
  const { configuration } = useOrgaConfigurationContext();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTogglingActive, setIsTogglingActive] = useState(false);
  const [addressValue, setAddressValue] = useState('');

  const form = useForm<GeneralConfigurationFormData>({
    defaultValues: {
      name: '',
      address: {
        street: '',
        city: '',
        postalCode: '',
        country: 'FR',
      },
      legalInfo: {
        companyName: '',
        legalId: '',
        vatNumber: '',
      },
    }
  });

  // Update form when configuration loads
  useEffect(() => {
    if (configuration) {
      const address = configuration.address;
      const legalInfo = configuration.legalInfo;

      // Priorité à l'adresse dans legalInfo pour l'affichage
      const effectiveAddress = legalInfo?.address || address;
      const fullAddress = `${effectiveAddress?.street || ''} ${effectiveAddress?.city || ''} ${effectiveAddress?.postalCode || ''}`.trim();

      form.reset({
        name: configuration.name || '',
        address: {
          street: effectiveAddress?.street || '',
          city: effectiveAddress?.city || '',
          postalCode: effectiveAddress?.postalCode || '',
          country: effectiveAddress?.country || 'FR',
        },
        legalInfo: {
          companyName: legalInfo?.companyName || '',
          legalId: legalInfo?.legalId || '',
          vatNumber: legalInfo?.vatNumber || '',
          address: legalInfo?.address || address,
        },
      });
      setAddressValue(fullAddress);
    }
  }, [configuration, form]);


  const handleAddressSelect = (street: string, city: string, postalCode: string, country: string) => {
    // Mettre à jour le formulaire avec les données de l'adresse
    form.setValue('address.street', street);
    form.setValue('address.city', city);
    form.setValue('address.postalCode', postalCode);
    form.setValue('address.country', country);

    // Mettre à jour la valeur affichée
    const fullAddress = `${street} ${city} ${postalCode}`.trim();
    setAddressValue(fullAddress);
  };

  const handleSave = async (data: GeneralConfigurationFormData) => {
    if (!idorga) {
      toast.error(t('admin.configuration.general.missingOrgId'));
      return;
    }

    setIsSaving(true);
    try {
      const updateData = {
        name: data.name,
        address: data.address,
        legalInfo: {
          ...data.legalInfo,
          address: data.address,
        },
      };

      await OrgaConfigurationRepository.update(idorga, updateData);
      toast.success(t('admin.configuration.general.updateSuccess'));
      setIsEditing(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error(t('admin.configuration.general.updateError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (configuration) {
      form.reset({
        name: configuration.name || '',
        address: {
          street: configuration.address?.street || '',
          city: configuration.address?.city || '',
          postalCode: configuration.address?.postalCode || '',
          country: configuration.address?.country || 'FR',
        },
        legalInfo: {
          companyName: configuration.legalInfo?.companyName || '',
          legalId: configuration.legalInfo?.legalId || '',
          vatNumber: configuration.legalInfo?.vatNumber || '',
        },
      });
    }
    setIsEditing(false);
  };

  const handleToggleActive = async () => {
    if (!idorga) {
      toast.error(t('admin.configuration.general.missingOrgId'));
      return;
    }

    const newActiveState = !configuration?.isActive;

    setIsTogglingActive(true);
    try {
      await OrgaConfigurationRepository.update(idorga, {
        isActive: newActiveState,
      });

      toast.success(
        newActiveState
          ? t('admin.configuration.general.activationSuccess')
          : t('admin.configuration.general.deactivationSuccess')
      );
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      toast.error(t('admin.configuration.general.statusChangeError'));
    } finally {
      setIsTogglingActive(false);
    }
  };

  return (
    <div className="space-y-6 min-h-[600px]">
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-foreground leading-tight">{t('admin.configuration.general.title')}</h2>
          <p className="text-xs sm:text-sm text-foreground-secondary mt-0.5 sm:mt-1">{t('admin.configuration.general.subtitle')}</p>
        </div>
        {!isEditing && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setIsEditing(true)}
            leftIcon={<Settings className="w-4 h-4" />}
            className="w-full sm:w-auto self-start sm:self-auto"
          >
            {t('admin.configuration.general.modify')}
          </Button>
        )}
      </div>

      <form onSubmit={form.handleSubmit(handleSave)} className="space-y-6">
        {/* Statut actif */}
        <Card variant="default">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-10 flex items-center justify-center">
                <Power className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">{t('admin.configuration.general.organizationActive', {
                  status: configuration?.isActive ? t('admin.configuration.general.active') : t('admin.configuration.general.inactive')
                })}</h3>
                <p className="text-xs text-foreground-secondary mt-0.5">
                  {configuration?.isActive
                    ? t('admin.configuration.general.activeDescription')
                    : t('admin.configuration.general.inactiveDescription')}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="flex items-center justify-between">
              <span className="text-sm text-foreground-secondary">
                {configuration?.isActive ? t('admin.configuration.general.active') : t('admin.configuration.general.inactive')}
              </span>
              <button
                onClick={handleToggleActive}
                disabled={isTogglingActive}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${configuration?.isActive ? 'bg-success' : 'bg-border-medium'
                  }`}
                title={configuration?.isActive ? t('admin.configuration.general.toggleActive') : t('admin.configuration.general.toggleInactive')}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${configuration?.isActive ? 'translate-x-6' : 'translate-x-1'
                    }`}
                />
              </button>
            </div>
          </CardBody>
        </Card>

        {/* Informations générales */}
        <Card variant="default">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-info-10 flex items-center justify-center">
                <Settings className="w-5 h-5 text-accent" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">{t('admin.configuration.general.basicInfo')}</h3>
                <p className="text-xs text-foreground-secondary mt-0.5">Nom de l'organisation</p>
              </div>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-foreground mb-1">
                {t('admin.configuration.general.name')} *
              </label>
              <input
                id="name"
                type="text"
                {...form.register('name', { required: t('admin.configuration.general.nameRequired') })}
                disabled={!isEditing}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary disabled:bg-background-tertiary disabled:text-foreground-secondary text-sm text-foreground bg-surface"
                placeholder={t('admin.configuration.general.namePlaceholder')}
              />
              {form.formState.errors.name && (
                <p className="mt-1 text-xs text-error">{form.formState.errors.name.message}</p>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Informations légales */}
        <Card variant="default">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning-10 flex items-center justify-center">
                <Scale className="w-5 h-5 text-warning" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">{t('admin.configuration.general.legalInfo.title')}</h3>
                <p className="text-xs text-foreground-secondary mt-0.5">{t('admin.configuration.general.legalInfo.subtitle')}</p>
              </div>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            {/* Message informatif */}
            <div className="bg-info-10 border border-info-20 rounded-lg p-4 flex gap-3">
              <Info className="w-5 h-5 text-info flex-shrink-0 mt-0.5" />
              <p className="text-sm text-info">
                {t('admin.configuration.general.legalInfo.autoGenerateInfo')}
              </p>
            </div>

            {/* Champs simplifiés */}
            <div className="space-y-4">
              {/* Nom légal de l'entreprise */}
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-foreground mb-1">
                  {t('admin.configuration.general.legalInfo.companyName')} *
                </label>
                <input
                  id="companyName"
                  type="text"
                  {...form.register('legalInfo.companyName', {
                    required: t('admin.configuration.general.legalInfo.companyNameRequired')
                  })}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary disabled:bg-background-tertiary disabled:text-foreground-secondary text-sm text-foreground bg-surface"
                  placeholder={t('admin.configuration.general.legalInfo.companyNamePlaceholder')}
                />
                {form.formState.errors.legalInfo?.companyName && (
                  <p className="mt-1 text-xs text-error">
                    {form.formState.errors.legalInfo.companyName.message}
                  </p>
                )}
              </div>

              {/* Identifiant légal / Numéro d'enregistrement */}
              <div>
                <label htmlFor="legalId" className="block text-sm font-medium text-foreground mb-1">
                  {t('admin.configuration.general.legalInfo.legalId')} *
                </label>
                <input
                  id="legalId"
                  type="text"
                  {...form.register('legalInfo.legalId', {
                    required: t('admin.configuration.general.legalInfo.legalIdRequired')
                  })}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary disabled:bg-background-tertiary disabled:text-foreground-secondary text-sm text-foreground bg-surface"
                  placeholder={t('admin.configuration.general.legalInfo.legalIdPlaceholder')}
                />
                {form.formState.errors.legalInfo?.legalId && (
                  <p className="mt-1 text-xs text-error">
                    {form.formState.errors.legalInfo.legalId.message}
                  </p>
                )}
              </div>

              {/* Numéro de TVA (optionnel) */}
              <div>
                <label htmlFor="vatNumber" className="block text-sm font-medium text-foreground mb-1">
                  {t('admin.configuration.general.legalInfo.vatNumber')}
                  <span className="text-foreground-secondary ml-1">({t('admin.configuration.general.optional')})</span>
                </label>
                <input
                  id="vatNumber"
                  type="text"
                  {...form.register('legalInfo.vatNumber')}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary disabled:bg-background-tertiary disabled:text-foreground-secondary text-sm text-foreground bg-surface"
                  placeholder={t('admin.configuration.general.legalInfo.vatNumberPlaceholder')}
                />
              </div>
            </div>

            {/* Séparateur */}
            <div className="border-t border-border pt-6">
              <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-info" />
                {t('admin.configuration.general.address')}
              </h4>

              {/* Recherche d'adresse */}
              <div className="space-y-4">
                <div>
                  <label htmlFor="address-search" className="block text-sm font-medium text-foreground mb-1">
                    {t('admin.configuration.general.searchAddress')}
                  </label>
                  {isEditing ? (
                    <GooglePlacesSearch
                      onAddressSelect={handleAddressSelect}
                      placeholder={t('admin.configuration.general.addressPlaceholder')}
                      autocompleteText={t('admin.configuration.general.addressAutocomplete')}
                      value={addressValue}
                      onChange={setAddressValue}
                      className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary text-sm text-foreground bg-surface"
                    />
                  ) : (
                    <input
                      id="address-search"
                      type="text"
                      value={addressValue ? addressValue : t('admin.configuration.general.addressNotDefined')}
                      disabled
                      className="w-full px-3 py-2 border border-border rounded-md bg-background-tertiary text-foreground-secondary text-sm"
                    />
                  )}
                </div>

                {/* Champs cachés pour le formulaire avec validation */}
                <input type="hidden" {...form.register('address.street', { required: true })} />
                <input type="hidden" {...form.register('address.city', { required: true })} />
                <input type="hidden" {...form.register('address.postalCode', { required: true })} />
                <input type="hidden" {...form.register('address.country', { required: true })} />

                {/* Message d'erreur si l'adresse est incomplète */}
                {(form.formState.errors.address?.street ||
                  form.formState.errors.address?.city ||
                  form.formState.errors.address?.postalCode) && (
                    <p className="mt-1 text-xs text-error">
                      {t('admin.configuration.general.addressRequired')}
                    </p>
                  )}

                {/* Affichage des détails de l'adresse */}
                {(form.watch('address.street') || form.watch('legalInfo.address.street')) && (
                  <div className="bg-background-tertiary border border-border rounded-lg p-4 space-y-2">
                    <p className="text-sm font-medium text-foreground">{t('admin.configuration.general.addressDetails')}</p>
                    <div className="text-sm text-foreground-secondary space-y-1">
                      <p><strong>{t('admin.configuration.general.street')}</strong> {form.watch('address.street') || form.watch('legalInfo.address.street')}</p>
                      <p><strong>{t('admin.configuration.general.city')}</strong> {form.watch('address.city') || form.watch('legalInfo.address.city')}</p>
                      <p><strong>{t('admin.configuration.general.postalCode')}</strong> {form.watch('address.postalCode') || form.watch('legalInfo.address.postalCode')}</p>
                      <p><strong>{t('admin.configuration.general.country')}</strong> {form.watch('address.country') || form.watch('legalInfo.address.country')}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Boutons d'action en bas du formulaire */}
        {isEditing && (
          <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 border-t border-border">
            <Button
              variant="ghost"
              size="md"
              onClick={handleCancel}
              className="w-full sm:w-auto"
            >
              {t('admin.configuration.general.cancel')}
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={form.handleSubmit(handleSave)}
              disabled={isSaving}
              isLoading={isSaving}
              className="w-full sm:w-auto"
            >
              {t('admin.configuration.general.save')}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default GeneralConfiguration;
